import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Divider from "@mui/material/Divider";

import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import dayjs from "dayjs";

import { getPassTypes, createPass, getTeacherDefaultLocations } from "../../services/PassService";
import { getSchoolTeachers } from "../../services/SchoolService";
import { getPassLocations } from "../../services/PassService";

import { hasError, getError } from "../../services/ServiceHelper";
import { showError, showToast } from "../../components/ATPDialogs";

const PassRequest = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);

  const [_teachers, setTeachers] = useState([]);
  const [_locations, setLocations] = useState([]);
  const [_passTypes, setPassTypes] = useState([]);
  const [_teacherDefaultLocations, setTeacherDefaultLocations] = useState([]);
  const [_toOption, setToOption] = useState("XX");

  const [_teacherEid, setTeacherEid] = useState("XX");
  const [_passTypeEid, setPassTypeEid] = useState("XX");
  const [_toLocationId, setToLocationId] = useState("XX");
  const [_toTeacherEid, setToTeacherEid] = useState("XX");
  const [_approvalCode, setApprovalCode] = useState("");

  const [_reason, setReason] = useState("");

  const evalPassTypes = (results) => {
    if (hasError(results) === true) {
      showToast("E", "Unable to get Pass Types: ", getError(results));
      return;
    }
    setPassTypes(results.data);
  };

  const evalSchoolTeachers = (results) => {
    if (hasError(results) === true) {
      showToast("E", "Unable to get Teachers: ", getError(results));
      return;
    }
    setTeachers(results.data);
  };

  const evalLocations = (results) => {
    if (hasError(results) === true) {
      showToast("E", "Unable to get Locations: ", getError(results));
      return;
    }
    setLocations(results.data);
  };

  const evalTeacherDefaultLocations = (results) => {
    if (hasError(results) === true) {
      showToast("E", "Unable to get Teacher Default Locations: ", getError(results));
      return;
    }

    // key: user eid, value: obj
    const map = {};

    for (const entry of results.data.default_bathroom_locations) {
      map[entry.userEid] = entry;
    }

    setTeacherDefaultLocations(map);
  };

  const initData = () => {
    setShowProgress(true);

    let promises = [
      getPassTypes(_school, _jwt),
      getSchoolTeachers(_school, _jwt),
      getPassLocations(_school, _jwt),
      getTeacherDefaultLocations(_school, _jwt)
    ];

    Promise.all(promises)
      .then((allResults) => {
        evalPassTypes(allResults[0]);
        evalSchoolTeachers(allResults[1]);
        evalLocations(allResults[2]);
        evalTeacherDefaultLocations(allResults[3]);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  useEffect(() => {
    initData();
  }, []);

  const savePass = (approvalCode = null) => {
    setShowProgress(true);
    let toLocId = _toLocationId;
    let toTeaId = _toTeacherEid;

    if (isRequiresLocation() && _toOption === "XX") {
      showError("Unable to Create Pass", "Please select where to Go To.");
      return;
    }

    if (_toOption === "L") {
      toTeaId = "";
    } else {
      toLocId = "";
    }

    if (toTeaId === "XX") toTeaId = "";
    if (toLocId === "XX") toLocId = "";

    // if (isBathroom()) {
    //   toTeaId = "";
    //   toLocId = "";
    // }

    createPass(
      _school,
      _jwt,
      _passTypeEid,
      dayjs().format("YYYY-MM-DD"),
      _teacherEid,
      toLocId,
      toTeaId,
      _reason,
      "",
      approvalCode,
      "N"
    )
      .then((result) => {
        if (result.detail) {
          window.showAlert("Unable to Create Pass: " + result.detail);
          return;
        }

        if (result.detail) {
          window.showAlert("Unable to Create Pass: " + result.detail);
          return;
        }
        if (approvalCode == null) {
          showToast("S", "Pass Created", "Please see teacher for approval.");
        } else {
          showToast(
            "S",
            "Pass Created",
            "Your pass has been created AND APPROVED."
          );
        }
        // reset the approval code
        setApprovalCode("");
      })
      .catch((error) => {
        showToast("E", "Unable to Create Pass");
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const getFirstBathroomPassType = () => {
    for (const passType of _passTypes) {
      if (passType.isBathroom === "Y") {
        return passType;
      }
    }
    return null;
  }

  const handleChangeTeacher = (event) => {
    const eid = event.target.value;

    setTeacherEid(eid);

    if (eid === "XX") {
      window.showAlert(null, "Please Select a Teacher.");
      return;
    }

    // see if there is a default bathroom for that teacher
    if (_teacherDefaultLocations[eid] != null) {
      const bathroomLocationId = _teacherDefaultLocations[eid].bathroomLocationId;
      if (bathroomLocationId != null) {
        setToLocationId(bathroomLocationId);
        setToOption("L");
        // get bathroom type
        const passType = getFirstBathroomPassType();
        console.log(passType);
        if (passType != null) {
          setPassTypeEid(passType.eid);
        }
      }
    }
  };

  const handleChangeReason = (event) => setReason(event.target.value);
  const handleChangePassType = (event) => setPassTypeEid(event.target.value);
  const handleChangeToLocation = (event) => setToLocationId(event.target.value);
  const handleChangeToTeacher = (event) => setToTeacherEid(event.target.value);
  const handleChangeApprovalCode = (event) =>
    setApprovalCode(event.target.value);

  const handleChangeToOption = (event) => {
    setToOption(event.target.value);
  };

  const handleSubmit = () => {
    if (_teacherEid === "XX") {
      window.showAlert("Please select a Teacher.");
      return;
    }

    if (_passTypeEid === "XX") {
      window.showAlert("Please select a Pass Type.");
      return;
    }

    if (!isBathroom()) {
      if (
        _toOption === "L" &&
        _toLocationId === "XX" &&
        _toTeacherEid === "XX"
      ) {
        window.showAlert("Please select a Location or a Teacher to go to.");
        return;
      }
    }

    // validate against previous pass
    savePass(null);
  };

  const handleApprovalCode = () => {
    if (_teacherEid === "") {
      window.showAlert("Please select a Teacher.");
      return;
    }

    if (_passTypeEid === "") {
      window.showAlert("Please select a Pass Type.");
      return;
    }

    if (!isBathroom()) {
      if (_toOption === "T" && _toTeacherEid === "XX") {
        window.showAlert("Please select a Teacher.");
        return;
      }
      if (_toOption === "L" && _toLocationId === "XX") {
        window.showAlert("Please select a Location.");
        return;
      }
    }

    if (_approvalCode === "") {
      window.showAlert("Please enter Teacher Approval Code.");
      return;
    }

    savePass(_approvalCode);
  };

  const getSelectedPassType = (passTypeEid) => {
    for (let i = 0; i < _passTypes.length; i++) {
      if (_passTypes[i].eid === passTypeEid) {
        return _passTypes[i];
      }
    }
    return null;
  };

  const isBathroom = () => {
    const passType = getSelectedPassType(_passTypeEid);
    if (passType == null) {
      return false;
    }

    // if pass type found (and not "")
    if (passType.isBathroom === "Y") {
      return true;
    }

    return false;
  };

  const isRequiresLocation = () => {
    const passType = getSelectedPassType(_passTypeEid);
    if (passType == null) {
      return false;
    }
    console.log(passType);

    // if pass type found (and not "")
    if (passType.requiresLocation === "Y") {
      return true;
    }

    return false;
  };

  const getAbbreviated = (name) => {
    return name.substring(0, 1) + ".";
  };

  const isButtonsEnabled = () => {
    return (
      _locations.length > 0 && _passTypes.length > 0 && _teachers.length > 0
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Request Pass" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        <Alert severity="info" key={alert.title}>
          <AlertTitle>Instructions</AlertTitle>
          Either click "Send Pass Request" to send to your teacher, or ask your
          teacher to enter their Approval Code, and click "Teacher Approved
          Pass."
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputLabel sx={{ mt: 1 }}>* Current Teacher:</InputLabel>
            <Select
              id="teacherEid"
              onChange={handleChangeTeacher}
              value={_teacherEid}
              size="small"
              fullWidth
            >
              <MenuItem value="XX">-- SELECT TEACHER --</MenuItem>
              {_teachers.map((teacher) => (
                <MenuItem key={teacher.eid} value={teacher.eid}>
                  {teacher.last_name}, {teacher.first_name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            {" "}
            <InputLabel sx={{ mt: 1 }}>* Pass Type:</InputLabel>
            <Select
              id="passTypeEid"
              onChange={handleChangePassType}
              value={_passTypeEid}
              size="small"
              fullWidth
              disabled={!isButtonsEnabled()}
            >
              <MenuItem value="XX">-- PASS TYPE --</MenuItem>
              {_passTypes.map((passType) => (
                <MenuItem key={passType.eid} value={passType.eid}>
                  {passType.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {isRequiresLocation() === true && _passTypeEid !== "XX" && (
            <>
              <Grid item xs={6}>
                <InputLabel sx={{ mt: 2 }}>* Go To:</InputLabel>
                <Select
                  id="toOption"
                  onChange={handleChangeToOption}
                  size="small"
                  value={_toOption}
                  fullWidth
                >
                  <MenuItem value="XX">-- SELECT --</MenuItem>
                  <MenuItem value="L">Location</MenuItem>
                  <MenuItem value="T">Teacher</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={6}>
                {_toOption === "L" && (
                  <>
                    <InputLabel sx={{ mt: 2 }}>* Select Location:</InputLabel>
                    <Select
                      id="toLocationId"
                      onChange={handleChangeToLocation}
                      value={_toLocationId}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="XX" key="XX">
                        -- LOCATION --
                      </MenuItem>
                      {_locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}

                {_toOption === "T" && (
                  <>
                    <>
                      <InputLabel sx={{ mt: 2 }}>* Select Teacher:</InputLabel>
                      <Select
                        id="toTeacherEid"
                        onChange={handleChangeToTeacher}
                        value={_toTeacherEid}
                        size="small"
                        fullWidth
                      >
                        <MenuItem value="XX" key="XX">
                          -- TEACHER --
                        </MenuItem>
                        {_teachers.map((teacher) => (
                          <MenuItem key={teacher.eid} value={teacher.eid}>
                            {teacher.last_name},{" "}
                            {getAbbreviated(teacher.first_name)}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  </>
                )}
              </Grid>
            </>
          )}
        </Grid>

        {isRequiresLocation() === true &&
          isBathroom() === false &&
          _passTypeEid !== "XX" && (
            <>
              <br />
              <InputLabel sx={{ mt: 0 }}>Reason (optional):</InputLabel>
              <TextField
                margin="dense"
                id="reaspn"
                type="text"
                fullWidth
                variant="outlined"
                onChange={handleChangeReason}
                placeholder="Optional"
                size="small"
                value={_reason}
              />
            </>
          )}

        <br />
        <Button
          onClick={handleSubmit}
          sx={{ mt: 1 }}
          variant="contained"
          disabled={!isButtonsEnabled()}
        >
          Send Pass Request
        </Button>

        <Divider sx={{ mt: 5, mb: 3 }} />
        <div>-- OR --</div>

        <InputLabel sx={{ mt: 2 }}>Enter Teacher Approval Code:</InputLabel>
        <TextField
          margin="dense"
          id="approvalCode"
          fullWidth
          variant="outlined"
          onChange={handleChangeApprovalCode}
          value={_approvalCode}
          type="password"
          size="small"
          autoComplete="off"
        />

        <Button
          onClick={handleApprovalCode}
          sx={{ mt: 1 }}
          variant="contained"
          disabled={!isButtonsEnabled()}
          color="secondary"
        >
          Teacher Approved Pass
        </Button>
      </Container>
    </>
  );
};

export default PassRequest;
